// Legend.js
/* eslint-disable react/prop-types */
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

// ---------------------------------------------------------------------------
// Allowed event types
// ---------------------------------------------------------------------------

export const EVENT_TYPES = [
  'Air/drone strike',
  'Shelling/artillery/missile attack',
  'Disrupted weapons use',
  'Other',
  'Peaceful protest',
  'Looting/property destruction',
  'Remote explosive/landmine/IED',
  'Armed clash',
  'Arrests',
];

// ---------------------------------------------------------------------------
// Color mapping
// ---------------------------------------------------------------------------

export const EVENT_COLORS = {
  'Air/drone strike': [228, 26, 28],
  'Shelling/artillery/missile attack': [55, 126, 184],
  'Disrupted weapons use': [77, 175, 74],
  Other: [152, 78, 163],
  'Peaceful protest': [255, 127, 0],
  'Looting/property destruction': [255, 255, 51],
  'Remote explosive/landmine/IED': [166, 86, 40],
  'Armed clash': [247, 129, 191],
  Arrests: [153, 153, 153],
};

const UNKNOWN_COLOR = [200, 200, 200];

export function getFillColorByEvent(eventType) {
  if (!eventType) return UNKNOWN_COLOR;
  return EVENT_COLORS[eventType] || UNKNOWN_COLOR;
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const LegendWrap = styled.aside`
  font-size: 13px;
  line-height: 1.2;
  color: #111;
  padding: 20px 0;
  max-width: 630px;
  user-select: none;
`;

const LegendTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #444;
`;

const LegendList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;

  @media (min-width: 610px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const LegendItem = styled.li`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const Swatch = styled.span`
  width: 14px;
  height: 14px;
  flex: 0 0 14px;
  border-radius: 3px;
  margin-right: 8px;
  border: 1px solid rgba(0, 0, 0, 0.15);
`;

const Label = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const toRGB = (arr) => `rgb(${arr[0]}, ${arr[1]}, ${arr[2]})`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Legend({
  title = 'Event type',
  includeUnknown = true,
  style,
}) {
  const eventtypes = useSelector((state) => state.data.eventtypes) || [];

  // Determine which event types exist in the data
  const allowedEvents = EVENT_TYPES.filter((type) => eventtypes.includes(type));

  // Detect if there are unknown types
  const hasUnknown = eventtypes.some((t) => !EVENT_TYPES.includes(t));

  return (
    <LegendWrap aria-label="Map legend: event type" style={style}>
      <LegendTitle>{title}</LegendTitle>

      <LegendList>
        {allowedEvents.map((event) => {
          const color = getFillColorByEvent(event);

          return (
            <LegendItem key={event}>
              <Swatch style={{ background: toRGB(color) }} aria-hidden="true" />
              <Label title={event}>{event}</Label>
            </LegendItem>
          );
        })}

        {includeUnknown && hasUnknown && (
          <LegendItem key="unknown">
            <Swatch
              style={{ background: toRGB(UNKNOWN_COLOR) }}
              aria-hidden="true"
            />
            <Label title="Unknown">Unknown</Label>
          </LegendItem>
        )}
      </LegendList>
    </LegendWrap>
  );
}
